import { IDatasource, IGetRowsParams } from 'ag-grid-community';
import { DataSource } from './entity-data-source.service';
import {
  Observable,
  observableFrom,
  observableOf,
  observableZip,
  rx,
} from '@shared/rx';
import { IDataSourceOptions } from '@shared/types/common';
import { BaseService } from '../base';
import { IBaseEntityType } from '@shared/types/entities';
import { isEmpty, isEqual, isString, map, merge, omit } from 'lodash';
import { XVIError } from '@shared/models';
import {
  IQuickFilterFieldSettings,
  IVisibleEntitiesStructure,
} from './data-source.interface';
import { Queue } from '@shared/utils/queue-utils';
import { FilterModel } from '@shared/types';

export class InfiniteScrollDataSource<
    TEntityService extends BaseService = BaseService,
  >
  extends DataSource
  implements IDatasource
{
  public rowCount: number;

  private getRowsCallsSequence: Queue<IGetRowsParams> =
    new Queue<IGetRowsParams>();
  private getRowsCallInProgress: boolean = false;
  private readonly defaultTotalCount: number = -1;

  public constructor(
    public service: TEntityService,
    options: IDataSourceOptions,
  ) {
    super(service, options);
  }

  public getRows(params: IGetRowsParams): void {
    this.getRowsCallsSequence.push(params);
    this.makeGetRowsCallFromSequence();
  }

  public getErrorObs(): Observable<XVIError> {
    return this.error.asObservable();
  }

  public isBusyObs(): Observable<boolean> {
    return this.busy.asObservable();
  }

  public composeFilterModel(text: string): FilterModel {
    if (!text) {
      const quickFilterFields = this.getQuickFilterFields();

      const listQuickFilterFields: string[] = map(
        quickFilterFields,
        (field: string | IQuickFilterFieldSettings) =>
          isString(field) ? field : field?.filedName,
      );

      return omit(this.filterModel, listQuickFilterFields);
    }

    const filterFields = super.composeFilterModel(text);

    return merge(this.filterModel, filterFields);
  }

  private makeGetRowsCallFromSequence(): void {
    if (this.getRowsCallInProgress || this.getRowsCallsSequence.isEmpty()) {
      return;
    }

    this.getRowsCallInProgress = true;
    const getRowsCallParameters = this.getRowsCallsSequence.getFront();
    this.processGetRowsCall(getRowsCallParameters);
  }

  private processGetRowsCall(params: IGetRowsParams): void {
    this.getVisibleItems(params).subscribe(
      ({ visibleEntities, total }) => {
        params.successCallback(visibleEntities, total);
        this.completeGetRowsProcessing();
      },
      () => this.completeGetRowsProcessing(),
    );
  }

  private completeGetRowsProcessing(): void {
    this.getRowsCallInProgress = false;
    this.getRowsCallsSequence.pop();
    this.makeGetRowsCallFromSequence();
  }

  private getVisibleItems(
    params: IGetRowsParams,
  ): Observable<IVisibleEntitiesStructure> {
    const { sortModel, filterModel } = params;
    // check for sort model or filter model changes. If something was changed - make a clean up of the data source
    if (
      this.isFilterModelChanged(filterModel) ||
      this.isSortModelChanged(sortModel)
    ) {
      this.purge();
    }
    this.sortModel = sortModel;
    this.filterModel = filterModel;

    return this.isBusyObs().pipe(
      rx.filter((isBusy) => !isBusy),
      rx.mergeMap(() =>
        observableZip(this.getAvailableItemsObs(), this.getExhaustedObs()),
      ),
      rx.first(),
      rx.mergeMap(([items, exhausted]) =>
        this.getVisibleFromAvailableEntities(params, items, exhausted),
      ),
      rx.first(),
    );
  }

  private getVisibleFromAvailableEntities(
    params: IGetRowsParams,
    availableItems: IBaseEntityType[],
    exhausted: boolean,
  ): Observable<IVisibleEntitiesStructure> {
    const { startRow, endRow } = params;
    const visibleEntities = availableItems.slice(startRow, endRow);
    if (this.shouldRequestNextPage(visibleEntities, exhausted)) {
      return this.requestNextBunchOfEntities(params);
    }

    return this.composeVisibleEntitiesResponse(
      visibleEntities,
      availableItems,
      exhausted,
    );
  }

  private shouldRequestNextPage(
    visibleEntities: IBaseEntityType[],
    exhausted: boolean,
  ): boolean {
    return (
      (isEmpty(visibleEntities) || visibleEntities.length < this.pageSize) &&
      !exhausted
    );
  }

  private composeVisibleEntitiesResponse(
    visibleEntities: IBaseEntityType[],
    availableItems: IBaseEntityType[],
    exhausted: boolean,
  ): Observable<IVisibleEntitiesStructure> {
    return observableOf({
      visibleEntities: this.selectedItemsMapper
        ? this.selectedItemsMapper(visibleEntities)
        : visibleEntities,
      total: exhausted ? availableItems.length : this.defaultTotalCount,
    });
  }

  private getNextPageObs(): Observable<IBaseEntityType[]> {
    return observableFrom(this.nextPage());
  }

  private requestNextBunchOfEntities(
    params: IGetRowsParams,
  ): Observable<IVisibleEntitiesStructure> {
    return this.getNextPageObs().pipe(
      rx.mergeMap(() => this.getVisibleItems(params)),
    );
  }

  private isFilterModelChanged(newSortModel): boolean {
    return !isEqual(this.filterModel, newSortModel);
  }

  private isSortModelChanged(newFilterModel): boolean {
    return !isEqual(this.sortModel, newFilterModel);
  }

  private getExhaustedObs(): Observable<boolean> {
    return this.exhausted.asObservable();
  }

  private getAvailableItemsObs(): Observable<IBaseEntityType[]> {
    return this.availableItems.asObservable();
  }
}
